import type { DashboardBranding } from "../theme/branding-types";

import { useDashboardBrandingVars } from "./use-dashboard-branding";

interface DashboardBrandingProviderProps {
  branding?: DashboardBranding | null;
  children: React.ReactNode;
}

/**
 * Scopes a client's branding to one dashboard's subtree.
 *
 * The app declares `--mb-color-*` on `:root` (see
 * `metabase/styled-components/theme/css-variables.ts`). Re-declaring the same custom
 * properties inline on a wrapper element shadows them for that subtree only, so ordinary
 * cascade does the work: no global mutation, nothing to clean up on unmount, and two
 * differently-branded dashboards can be open at once without interfering.
 *
 * Scoping is the point. Writing these to `:root` would leak one client's colors into the
 * nav, the admin section, and every other dashboard in the session.
 *
 * Inline styles rather than an injected <style> element: they win against `:root` on
 * specificity without needing a selector, and they cannot silently fail to parse. The
 * first version of this component injected a <style> via dangerouslySetInnerHTML and was
 * completely inert in a real browser -- the element mounted with correct CSS but its
 * `.sheet` was null, so none of it ever applied.
 *
 * Renders a plain passthrough when there is no branding, so unbranded dashboards keep
 * inheriting the AGAVE theme and pay nothing for the feature.
 */
export function DashboardBrandingProvider({
  branding,
  children,
}: DashboardBrandingProviderProps) {
  const vars = useDashboardBrandingVars(branding);

  if (!vars) {
    return <>{children}</>;
  }

  return (
    <div style={vars} data-testid="agavepulse-branding-scope">
      {children}
    </div>
  );
}
